import { useEffect, useRef } from "react";
import {
  registerFocusRevalidation,
  REVALIDATE_MIN_INTERVAL_MS,
} from "@/lib/realtime/revalidateOnFocus";

export interface UseRevalidateOnFocusOptions {
  enabled?: boolean;
  minIntervalMs?: number;
}

/**
 * Re-read something when the user comes back to the tab or the network
 * returns. See `@/lib/realtime/revalidateOnFocus` for why this is required
 * even on surfaces that are fully subscribed to realtime.
 *
 * `revalidate` is held in a ref, so an inline arrow at the call site does not
 * re-register the listeners on every render.
 */
export function useRevalidateOnFocus(
  revalidate: () => void,
  opts: UseRevalidateOnFocusOptions = {}
): void {
  const { enabled = true, minIntervalMs = REVALIDATE_MIN_INTERVAL_MS } = opts;

  const revalidateRef = useRef(revalidate);
  // In an effect, not during render: a render that never commits must not be
  // what the listener ends up calling.
  useEffect(() => {
    revalidateRef.current = revalidate;
  });

  useEffect(() => {
    if (!enabled) return;
    return registerFocusRevalidation(() => revalidateRef.current(), { minIntervalMs });
  }, [enabled, minIntervalMs]);
}
